import { useState } from "react";
import { PostType } from "../TextGeneration/contentGenerator";
import {
  loadImageElement,
  analyzeImageProperties,
  Orientation,
  Brightness,
} from "./imageProperties";

import {
  useAuth,
} from "../Authentification/AuthContext";

import {
  createAuthFetch,
} from "../Authentification/apiFetch";

export interface FileAnalysisResult {
  fileName: string;
  postPotential:
    | "high"
    | "medium"
    | "low"
    | "none";
  suggestedPostTypes: PostType[];
  suggestedImageTemplates: string[];
  suggestedUsage: string;
  relatedFiles: string[];
  reasoning: string;
}

interface FileAnalyserProps {
  files: File[];
  language: string;
  usecases: string[];
  onAnalysisComplete: (
    results: FileAnalysisResult[]
  ) => void;
  externalBusy?: boolean;
  externalBusyLabel?: string;
  onAnalysisDuration?: (
    durationMs: number
  ) => void;
}

type Branding =
  | "GameDays"
  | "AG";

export default function FileAnalyser({
  files,
  language,
  usecases,
  onAnalysisComplete,
  externalBusy = false,
  externalBusyLabel = "",
  onAnalysisDuration,
}: FileAnalyserProps) {
  const { token } = useAuth();

  const apiFetch =
    createAuthFetch(token);

  const [
    isLoadingState,
    setIsLoadingState,
  ] = useState<string>(
    "Keine Anfrage"
  );

  const [error, setError] =
    useState<string | null>(null);

  try {
  } finally {
  }

  const isOwnBusy =
    isLoadingState !==
    "Keine Anfrage";

  const isBusy =
    isOwnBusy ||
    externalBusy;

  const buttonLabel =
    isOwnBusy
      ? isLoadingState
      : externalBusy
        ? (
            externalBusyLabel ||
            "Posts werden generiert..."
          )
        : "Daten analysieren";

  function isImageFile(
    file: File
  ): boolean {
    return file.type.startsWith(
      "image/"
    );
  }

  function fixMojibake(
    str: string
  ): string {
    // Falls irgendein Zeichen außerhalb des Latin-1-Bereichs liegt,
    // ist es vermutlich gar kein Mojibake (z.B. Emoji).
    if (
      !/^[\u0000-\u00FF]*$/.test(
        str
      )
    ) {
      return str;
    }

    try {
      const bytes =
        Uint8Array.from(
          str,
          (ch) =>
            ch.charCodeAt(0)
        );

      const decoded =
        new TextDecoder(
          "utf-8",
          {
            fatal: true,
          }
        ).decode(bytes);

      return decoded;
    } catch {
      // War kein gültiges UTF-8.
      return str;
    }
  }

  // Erkennt anhand des Dateinamens,
  // ob es sich um GameDays- oder
  // AG/Arbeitsgruppe-Branding handelt.
  function detectBranding(
    fileName: string
  ): Branding | null {
    const lower =
      fileName.toLowerCase();

    if (
      lower.includes(
        "gamedays"
      )
    ) {
      return "GameDays";
    }

    if (
      lower.includes(
        "arbeitsgruppe"
      ) ||
      /(^|[^a-z])ag([^a-z]|$)/.test(
        lower
      )
    ) {
      return "AG";
    }

    return "AG";
  }

  function selectFirstSpiritImageTemplate(
    fileName: string,
    orientation: Orientation,
    brightness: Brightness
  ): string | null {
    const branding =
      detectBranding(fileName);

    if (!branding) {
      return null;
    }

    const orientationLabel =
      orientation ===
      "landscape"
        ? "Landscape"
        : "Portrait";

    // bewusst invertiert
    // für Kontrast
    const brightnessLabel =
      brightness === "light"
        ? "Dark"
        : "Light";

    return `${branding} ${orientationLabel} Format ${brightnessLabel}`;
  }

  async function analyzeFirstSpiritImage(
    file: File
  ): Promise<FileAnalysisResult> {
    try {
      if (
        file.name.includes(
          "Referent"
        ) ||
        file.name.includes(
          "referent"
        ) ||
        file.name.includes(
          "Speaker"
        ) ||
        file.name.includes(
          "speaker"
        )
      ) {
        return {
          fileName: file.name,
          postPotential:
            "high",
          suggestedPostTypes:
            [],
          suggestedImageTemplates:
            [
              "Webseite_Referenten",
            ],
          suggestedUsage:
            "Automatische Skalierung des Referentenbilds für Webseite",
          relatedFiles: [],
          reasoning:
            "Dateiname deutet auf Referent auf Webseite hin.",
        };
      }

      const img =
        await loadImageElement(
          file
        );

      const {
        orientation,
        brightness,
      } =
        analyzeImageProperties(
          img
        );

      const template =
        selectFirstSpiritImageTemplate(
          file.name,
          orientation,
          brightness
        );

      if (!template) {
        return {
          fileName:
            file.name,
          postPotential:
            "low",
          suggestedPostTypes:
            [],
          suggestedImageTemplates:
            [],
          suggestedUsage:
            "Dateiname enthält weder 'GameDays' noch 'AG'/'Arbeitsgruppe' - keine automatische Template-Zuordnung möglich",
          relatedFiles: [],
          reasoning:
            "Weder Format noch Helligkeit lassen sich ohne erkennbares Branding im Dateinamen einem FirstSpirit-Template zuordnen.",
        };
      }

      return {
        fileName:
          file.name,
        postPotential:
          "high",
        suggestedPostTypes:
          [],
        suggestedImageTemplates:
          [template],
        suggestedUsage:
          `${
            orientation ===
            "landscape"
              ? "Querformat"
              : "Hochformat"
          }, ${
            brightness ===
            "light"
              ? "helles"
              : "dunkles"
          } Bild - passend zum Template "${template}"`,
        relatedFiles: [],
        reasoning:
          `Dateiname deutet auf ${
            template.startsWith(
              "GameDays"
            )
              ? "GameDays"
              : "AG"
          }-Branding hin, Bildmaße ergeben ${
            orientation ===
            "landscape"
              ? "Querformat"
              : "Hochformat"
          }, durchschnittliche Helligkeit ergibt "${brightness}".`,
      };
    } catch (err) {
      return {
        fileName: file.name,
        postPotential:
          "none",
        suggestedPostTypes:
          [],
        suggestedImageTemplates:
          [],
        suggestedUsage:
          "Bild konnte nicht analysiert werden",
        relatedFiles: [],
        reasoning:
          err instanceof Error
            ? err.message
            : "Unbekannter Fehler beim Laden des Bildes",
      };
    }
  }

  function analyzeFirstSpiritTextFile(
    file: File
  ): FileAnalysisResult {
    if (
      file.name.includes(
        "Referent"
      ) ||
      file.name.includes(
        "referent"
      ) ||
      file.name.includes(
        "Speaker"
      ) ||
      file.name.includes(
        "speaker"
      )
    ) {
      return {
        fileName: file.name,
        postPotential:
          "high",
        suggestedPostTypes:
          [
            "first_spirit_referenten",
          ],
        suggestedImageTemplates:
          [],
        suggestedUsage:
          "Automatische Tokenerkennung und Aufbereitung für Webseiten-Fehler",
        relatedFiles: [],
        reasoning:
          "Dateiname deutet auf Referent auf Webseite hin.",
      };
    }

    return {
      fileName: file.name,
      postPotential:
        "high",
      suggestedPostTypes:
        ["first_spirit"],
      suggestedImageTemplates:
        [],
      suggestedUsage:
        "Text-Datei - direkt als Quelle für einen first_spirit-Post verwenden",
      relatedFiles: [],
      reasoning:
        "FirstSpirit-Usecase aktiv: Text-/Datendateien werden ohne KI-Analyse direkt dem first_spirit-Posttyp zugeordnet.",
    };
  }

  async function runFirstSpiritAnalysis():
    Promise<
      FileAnalysisResult[]
    > {
    const results:
      FileAnalysisResult[] =
      [];

    for (
      let i = 0;
      i < files.length;
      i++
    ) {
      const file =
        files[i];

      setIsLoadingState(
        `FirstSpirit-Zuordnung ${i + 1}/${files.length}: ${file.name}`
      );

      if (
        isImageFile(file)
      ) {
        results.push(
          await analyzeFirstSpiritImage(
            file
          )
        );
      } else {
        results.push(
          analyzeFirstSpiritTextFile(
            file
          )
        );
      }
    }

    return results;
  }

  async function analyzeSingleFileViaAI(
    file: File,
    otherFileNames: string[]
  ): Promise<
    FileAnalysisResult[]
  > {
    const formData =
      new FormData();

    formData.append(
      "files",
      file
    );

    formData.append(
      "language",
      language
    );

    formData.append(
      "otherFileNames",
      JSON.stringify(
        otherFileNames
      )
    );

    const response =
      await apiFetch(
        "/api/analyze-files",
        {
          method: "POST",
          body: formData,

          // Kein Content-Type setzen.
          // Der Browser setzt bei
          // FormData automatisch
          // multipart/form-data
          // inklusive Boundary.
        }
      );

    if (!response.ok) {
      const errorData =
        await response
          .json()
          .catch(
            () => null
          );

      throw new Error(
        errorData?.details ??
          errorData?.error ??
          `Serverfehler (${response.status})`
      );
    }

    const data =
      await response.json();

    const analysis =
      (
        data.analysis as
          FileAnalysisResult[]
      ).map(
        (result) => ({
          ...result,

          fileName:
            fixMojibake(
              result.fileName
            ),

          relatedFiles:
            result.relatedFiles.map(
              fixMojibake
            ),
        })
      );

    return analysis;
  }

  async function runAIAnalysis():
    Promise<
      FileAnalysisResult[]
    > {
    const results:
      FileAnalysisResult[] =
      [];

    const allFileNames =
      files.map(
        (f) => f.name
      );

    for (
      let i = 0;
      i < files.length;
      i++
    ) {
      const file =
        files[i];

      const otherFileNames =
        allFileNames.filter(
          (_, idx) =>
            idx !== i
        );

      setIsLoadingState(
        `Analysiere Datei ${i + 1}/${files.length}: ${file.name}`
      );

      try {
        const fileResults =
          await analyzeSingleFileViaAI(
            file,
            otherFileNames
          );

        results.push(
          ...fileResults
        );
      } catch (err) {
        console.error(
          `Analyse von "${file.name}" fehlgeschlagen:`,
          err
        );

        setError(
          err instanceof Error
            ? `Analyse von "${file.name}" fehlgeschlagen: ${err.message}`
            : `Analyse von "${file.name}" fehlgeschlagen: Unbekannter Fehler`
        );

        // Trotz Fehler bei einer Datei
        // mit den restlichen weitermachen.
      }
    }

    return results;
  }

  async function checkUseCase() {
    if (
      files.length === 0
    ) {
      setError(
        "Bitte zuerst Dateien hochladen."
      );
      return;
    }

    const analysisStartedAt =
      performance.now();

    try {
      setError(null);

      const combinedResults:
        FileAnalysisResult[] =
        [];

      const wantsAIAnalysis =
        usecases.includes(
          "LinkedInAG"
        ) ||
        usecases.includes(
          "LinkedInGameDays"
        ) ||
        usecases.includes(
          "LinkedInPersonal"
        );

      const wantsFirstSpirit =
        usecases.includes(
          "FirstSpiritAG"
        ) ||
        usecases.includes(
          "FirstSpiritTU"
        );

      if (
        wantsAIAnalysis
      ) {
        combinedResults.push(
          ...(await runAIAnalysis())
        );
      }

      if (
        wantsFirstSpirit
      ) {
        combinedResults.push(
          ...(await runFirstSpiritAnalysis())
        );
      }

      onAnalysisComplete(
        combinedResults
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Analyse fehlgeschlagen"
      );
    } finally {
      const analysisDurationMs =
        performance.now() -
        analysisStartedAt;

      console.log(
        "EVALUATION analysisDurationMs:",
        Math.round(
          analysisDurationMs
        )
      );

      onAnalysisDuration?.(
        analysisDurationMs
      );

      setIsLoadingState(
        "Keine Anfrage"
      );
    }
  }

  return (
    <div className="file-analyser-wrapper">
      <button
        onClick={
          checkUseCase
        }
        disabled={
          isBusy ||
          usecases.length ===
            0 ||
          files.length === 0
        }
        className="btn-primary"
      >
        {buttonLabel}
      </button>

      {error && (
        <span className="file-input-error">
          {error}
        </span>
      )}
    </div>
  );
}